import React, { useMemo, useState } from 'react';
import {
  Badge, Box, Button, Chip, CircularProgress, Collapse, Divider, InputAdornment, Paper, TextField, Tooltip, Typography
} from '@mui/material';
import {
  FileDownload as DownloadIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import ProductFilter from '../../Documents/ProductFilter';
import { countAdvancedFilters, hasAnyFilter } from '../constants';
import { searchMaterials } from '../markingApi';
import { MarkingFilters, MarkingMeta } from '../types';
import { IdMultiSelect, PlaceFilter, summary } from './FilterControls';

interface Props {
  meta: MarkingMeta;
  filters: MarkingFilters;
  loading: boolean;
  onChange: (patch: Partial<MarkingFilters>) => void;
  onReset: () => void;
  onRefresh: () => void;
  onExport: () => void;
}

const chipSx = (color: string, bg: string) => ({ bgcolor: bg, color, fontWeight: 600 });

const MarkingCodesToolbar: React.FC<Props> = ({ meta, filters, loading, onChange, onReset, onRefresh, onExport }) => {
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const pharmacyOptions = useMemo(() => meta.pharmacies.filter(p => p.kind === 'pharmacy'), [meta.pharmacies]);
  const storageOptions = useMemo(() => meta.pharmacies.filter(p => p.kind === 'warehouse'), [meta.pharmacies]);
  const typeNames = useMemo(() => {
    const map: Record<number, string> = {};
    meta.types.forEach(t => { map[t.id] = t.name; });
    return map;
  }, [meta.types]);
  const statusNames = useMemo(() => {
    const map: Record<number, string> = {};
    meta.statuses.forEach(s => { map[s.id] = s.name; });
    return map;
  }, [meta.statuses]);
  const storageNames = useMemo(() => {
    const map: Record<string, string> = {};
    storageOptions.forEach(s => { map[s.guid] = s.name; });
    return map;
  }, [storageOptions]);

  const advancedCount = countAdvancedFilters(filters);

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', flexShrink: 0 }}>
          <FilterListIcon fontSize="small" />
          <Typography variant="subtitle2" fontWeight={600}>Фильтр:</Typography>
        </Box>

        <TextField
          size="small" placeholder="Код, № документа, товар, аптека…" value={filters.search}
          onChange={e => onChange({ search: e.target.value })} sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>
          }}
        />

        <PlaceFilter
          label="Аптеки" emptyHint="Все аптеки зоны" noOptions="Аптека не найдена" minWidth={240} maxWidth={420}
          options={pharmacyOptions} value={filters.pharmacies} onChange={pharmacies => onChange({ pharmacies })}
        />

        <Badge badgeContent={advancedCount} color="primary">
          <Tooltip title={advancedOpen ? 'Скрыть фильтры' : 'Показать дополнительные фильтры'}>
            <Button
              variant={advancedOpen ? 'contained' : 'outlined'} color={advancedOpen ? 'primary' : 'inherit'} size="small"
              onClick={() => setAdvancedOpen(o => !o)} aria-label="Дополнительные фильтры"
              sx={{ height: 40, minWidth: 40, width: 40, p: 0, borderRadius: 1.5, borderColor: 'rgba(0, 0, 0, 0.23)' }}
            >
              <TuneIcon fontSize="small" />
            </Button>
          </Tooltip>
        </Badge>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Button
            variant="outlined" color="inherit" size="small" onClick={onReset} disabled={!hasAnyFilter(filters)}
            sx={{ height: 40, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, borderColor: '#c4c4c4' }}
          >
            Сброс
          </Button>
          <Button
            variant="contained" size="small" onClick={onRefresh} disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            sx={{ height: 40, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, px: 2 }}
          >
            Обновить
          </Button>
          {meta.access.export && (
            <Tooltip title="Выгрузка кодов в Excel / CSV">
              <Button
                variant="outlined" size="small" color="success" onClick={onExport}
                startIcon={<DownloadIcon />}
                sx={{ height: 40, textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
              >
                Выгрузка
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>

      {advancedCount > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          {filters.types.length > 0 && (
            <Chip
              size="small" label={`Тип: ${summary(filters.types.map(id => typeNames[id] || String(id)))}`}
              onDelete={() => onChange({ types: [] })} sx={chipSx('#10b981', 'rgba(16,185,129,0.08)')}
            />
          )}
          {filters.statuses.length > 0 && (
            <Chip
              size="small" label={`Статус: ${summary(filters.statuses.map(id => statusNames[id] || String(id)))}`}
              onDelete={() => onChange({ statuses: [] })} sx={chipSx('#ea580c', 'rgba(249,115,22,0.08)')}
            />
          )}
          {filters.storages.length > 0 && (
            <Chip
              size="small" label={`Склад: ${summary(filters.storages.map(g => storageNames[g] || g))}`}
              onDelete={() => onChange({ storages: [] })} sx={chipSx('#0284c7', 'rgba(14,165,233,0.08)')}
            />
          )}
          {filters.products.length > 0 && (
            <Chip
              size="small" label={`Товар: ${summary(filters.products.map(p => p.name))}`}
              onDelete={() => onChange({ products: [] })} sx={chipSx('#7c3aed', 'rgba(139,92,246,0.08)')}
            />
          )}
          {(filters.dateFrom || filters.dateTo) && (
            <Chip
              size="small" label={`Период: ${filters.dateFrom || '…'} - ${filters.dateTo || '…'}`}
              onDelete={() => onChange({ dateFrom: '', dateTo: '' })} sx={chipSx('#d97706', 'rgba(245,158,11,0.08)')}
            />
          )}
        </Box>
      )}

      <Collapse in={advancedOpen} timeout={200} unmountOnExit>
        <Divider sx={{ my: 1.25 }} />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.25 }}>
          <IdMultiSelect label="Тип" allText="Все типы" options={meta.types} value={filters.types} onChange={types => onChange({ types })} />
          <IdMultiSelect
            label="Статус" allText="Все статусы" minWidth={220}
            options={meta.statuses} value={filters.statuses} onChange={statuses => onChange({ statuses })}
          />

          {storageOptions.length > 0 && (
            <PlaceFilter
              label="Склады" emptyHint="Все склады" noOptions="Склад не найден" maxWidth={360}
              options={storageOptions} value={filters.storages} onChange={storages => onChange({ storages })}
            />
          )}

          <TextField
            size="small" type="date" label="Дата с" value={filters.dateFrom}
            onChange={e => onChange({ dateFrom: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
          />
          <TextField
            size="small" type="date" label="Дата по" value={filters.dateTo}
            onChange={e => onChange({ dateTo: e.target.value })} InputLabelProps={{ shrink: true }}
            inputProps={{ min: filters.dateFrom || undefined }} sx={{ width: 160 }}
          />

          <Box sx={{ minWidth: 280, flex: '1 1 320px' }}>
            <ProductFilter
              text="" products={filters.products} search={searchMaterials} productsOnly
              onChange={next => onChange({ products: next.products })}
            />
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default React.memo(MarkingCodesToolbar);
